import { formatInTimeZone } from "date-fns-tz";
import { Link } from "react-router-dom";
import { Icon, Tooltip, Typography } from "@/components/atoms";
import { DataTableColumnDef, DataTableRow } from "@/components/molecules";
import { getManageClientPath } from "@/components/organisms/Clients/helpers";
import { driverDelayCaption, getOldBookingUrl, getOperatorBookingSheetUrl, pickupReconcilesAsSum } from "@/helpers/bookingHelpers";
import {
  Booking,
  BookingCloseMeta,
  BookingDriver,
  BookingFlight,
  BookingStatus,
  BookingStop,
  BookingVehicle,
} from "@/redux/slices/booking/types";
import { Profile } from "@/redux/slices/profile/types";
import { formatCurrency } from "@/utils";
import { BookingFlightNumber, BookingNotFlying } from "../common/BookingFlightNumber/BookingFlightNumber";
import { BookingStatusIcon } from "../common/BookingStatusIcon/BookingStatusIcon";
import { paymentMethods, paymentStatuses, statusMapping } from "../fixtures";
import { PaymentStatusTooltip } from "../tooltips/PaymentStatusTooltip";
import { BookingsTableData, TableColumn } from "../types";
import { getVehicleWarningTooltip } from "./driver";
import { arrivalFlightOf, departureFlightOf } from "./stopFlights";

export const getRowClasses = (row: DataTableRow<BookingsTableData>) => {
  return row.getIsExpanded() ? "bg-primary-light" : "bg-white";
};

/**
 * Smallest width a booking column may be dragged to: roughly its own header text, so the label stays
 * readable and everything past it ellipses.
 *
 * This estimates rather than measures. The alternative — rendering each header and reading it back —
 * needs a DOM pass on every column before the table can size itself, and the numbers only have to be
 * good enough to stop a drag, not to lay anything out. Tuned against the header's own styling:
 * `text-base` (14px per our scale, not Tailwind's) semibold, `p-2` either side, plus the sort chevron
 * and — on Payment Status alone — its info tooltip.
 *
 * Previously `minSize` was set to the column's DEFAULT width, which meant no column could ever be
 * dragged narrower than it started. On a wide monitor the two address columns default to roughly half
 * the leftover screen each, so they arrived ~970px wide and pinned there.
 */
const ICON_COLUMN_WIDTH = 32;
const AVERAGE_CHARACTER_WIDTH = 7.5;
const HORIZONTAL_PADDING = 16;
const SORT_ICON_WIDTH = 20;
const TOOLTIP_ICON_WIDTH = 24;

const getMinColumnWidth = (column: TableColumn) => {
  const label = Math.ceil(column.header.length * AVERAGE_CHARACTER_WIDTH);
  const sortIcon = column.isSortDisabled ? 0 : SORT_ICON_WIDTH;
  const tooltipIcon = column.id === "paymentStatus" ? TOOLTIP_ICON_WIDTH : 0;

  // The floor keeps headerless columns (the offload icon) from collapsing onto their glyph.
  return Math.max(ICON_COLUMN_WIDTH, label + HORIZONTAL_PADDING + sortIcon + tooltipIcon);
};

export const generateBookingColumns = (tableColumns: TableColumn[]) => {
  const columns: DataTableColumnDef<BookingsTableData>[] = tableColumns.map(
    (column) =>
      ({
        accessorKey: column.id,
        id: column.sortId || column.id,
        header: () =>
          column.id !== "paymentStatus" ? (
            <Typography variant="action" className="line-clamp-1 inline">
              {column.header}
            </Typography>
          ) : (
            <div className="inline-flex place-items-center gap-2">
              <Typography variant="action" className="line-clamp-1">
                {column.header}
              </Typography>
              <Tooltip content={<PaymentStatusTooltip />} placement="bottom">
                <Icon name="InfoCircle" variant="Bold" className="rotate-180 text-neutral-dark-gray" size="sm" />
              </Tooltip>
            </div>
          ),
        cell: ({ row }) => {
          const value = row.original[column.id];

          // Plain-string cells (Cost, Payment Status, Payment Method, Notes) have no element of their
          // own to ellipse. JSX cells bring their own truncation and are often two lines, so wrapping
          // those in a nowrap span would flatten them.
          return typeof value === "string" ? <span className="block truncate">{value}</span> : value;
        },
        minSize: getMinColumnWidth(column),
        ...(column.size && { size: column.size }),
        enableSorting: !column.isSortDisabled,
      } as DataTableColumnDef<BookingsTableData>)
  );

  return columns;
};

/**
 * `operatorId` is what turns the job-number cell into an in-app link. Optional so the two callers can
 * be migrated independently and so the existing tests keep their three-argument calls; without it the
 * cell falls back to the legacy PHP page rather than building a route with `undefined` in it.
 */
export const generateBookingData = (bookings: Booking[], profile: Profile, providerKey: string, operatorId?: string) => {
  const data = bookings.map(
    (booking) =>
      ({
        bookingId: booking.id,
        offloadIcon: <span>{renderOffloadStatus(booking, providerKey)}</span>,
        id: (
          <>
            <div className="group/cell flex items-center gap-2 overflow-ellipsis">
              <Typography
                variant="paragraph"
                className="min-w-0 truncate leading-tight group-hover/cell:text-info group-[.compact-view]:flex sm:leading-none"
              >
                {booking?.account ? (
                  /*
                   * The job number opens the NEW operator booking sheet — the same destination as
                   * "View Booking Sheet" on the booking panel (DEV-1524), so the two ways into a
                   * booking no longer land in two different UIs.
                   *
                   * A plain anchor, not a react-router Link: getOperatorBookingSheetUrl returns an
                   * absolute URL (window.location.origin + /dashboard/...), which Link would try to
                   * resolve as a route path.
                   */
                  <a
                    href={
                      operatorId
                        ? getOperatorBookingSheetUrl(operatorId, booking.account.id, booking.id)
                        : getOldBookingUrl(booking.account.id, booking.id, "").replace("/admin/", "")
                    }
                    className="relative z-10"
                    onClick={(e) => e.stopPropagation()}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {booking.jobNumber}
                  </a>
                ) : (
                  booking.jobNumber
                )}
                {["daily_rate", "hourly_rate"].includes(booking.transferType.type) && (
                  <Typography
                    variant="small"
                    className="hidden capitalize leading-4 text-neutral-dark-gray group-[.compact-view]:ml-1 sm:block"
                  >
                    {booking.transferType.type.replace("_", " ").replace("rate", "")}
                  </Typography>
                )}
              </Typography>
            </div>
            <Typography variant="small" className="block leading-normal text-neutral-dark-gray sm:hidden">
              ID
            </Typography>
          </>
        ),
        date: (
          <>
            <Typography className="truncate leading-tight">
              {formatInTimeZone(new Date(booking.dates.pickup), booking.dates.timezone, profile.dateFormat)}
            </Typography>
            <Typography variant="small" className="block leading-normal text-neutral-dark-gray sm:hidden">
              Date
            </Typography>
          </>
        ),
        dateAndTime: (
          <div className="flex flex-col gap-y-0.5">
            <Typography variant="paragraph">
              {formatInTimeZone(new Date(booking.dates.pickup), booking.dates.timezone, profile.dateFormat)}
            </Typography>
            <div className="flex flex-row items-center gap-x-0.5 sm:gap-x-2.5">
              <Typography className="truncate leading-tight ">
                {formatInTimeZone(new Date(booking.dates.pickup), booking.dates.timezone, profile.timeFormat)}
              </Typography>
              {profile.timezone.name !== booking.dates.timezone && (
                <Typography variant="small-regular" className="whitespace-nowrap leading-snug text-neutral-dark-gray">
                  <span className="hidden group-[.compact-view]:hidden sm:inline"> ({booking.dates.timezone.split("/")[1]})</span>
                  <Tooltip
                    content={formatInTimeZone(new Date(booking.dates.pickup), profile.timezone.name, `${profile.timeFormat} zzzz`)}
                    placement="bottom"
                  >
                    <Icon name="InfoCircle" size="md" className="ml-1 inline rotate-180" />
                  </Tooltip>
                </Typography>
              )}
            </div>
            {/* The same caption the `time` cell carries. This column is the combined REPLACEMENT for
                `date` + `time`, so an operator who picks it would otherwise lose the delay provenance
                entirely — and that choice now persists across sessions. The cell is already a flex
                column, so no extra wrapper is needed to stack it. */}
            {renderPickupDelayCaption(booking, profile)}
            <Typography variant="small-regular" className="block leading-normal text-neutral-dark-gray sm:hidden">
              Date and Time
            </Typography>
          </div>
        ),
        time: (
          <>
            {/* The cell itself is a flex ROW, so the caption below has to be wrapped with the time in a
                column or it lands on the same line as the time (design node `11784:129863` stacks them). */}
            <div className="flex flex-col">
              <div className="flex group-[.compact-view]:items-center group-[.compact-view]:justify-normal sm:flex-col sm:justify-center group-[.compact-view]:sm:flex-row">
                <Typography className="truncate leading-tight ">
                  {formatInTimeZone(new Date(booking.dates.pickup), booking.dates.timezone, profile.timeFormat)}
                </Typography>
                {profile.timezone.name !== booking.dates.timezone && (
                  <Typography variant="small-regular" className="whitespace-nowrap leading-snug text-neutral-dark-gray">
                    <span className="hidden group-[.compact-view]:hidden sm:inline"> ({booking.dates.timezone.split("/")[1]})</span>
                    <Tooltip
                      content={formatInTimeZone(new Date(booking.dates.pickup), profile.timezone.name, `${profile.timeFormat} zzzz`)}
                      placement="bottom"
                    >
                      <Icon name="InfoCircle" size="md" className="ml-1 inline rotate-180" />
                    </Tooltip>
                  </Typography>
                )}
              </div>
              {renderPickupDelayCaption(booking, profile)}
            </div>
            <Typography variant="small" className="block leading-normal text-neutral-dark-gray sm:hidden">
              Time
            </Typography>
          </>
        ),
        passenger: renderPassenger(booking, operatorId),
        client: <Typography className="truncate">{booking.account?.name || "-"}</Typography>,
        bookedBy: <Typography className="truncate">{booking.bookedBy || "-"}</Typography>,
        operatorIdentifier: <Typography className="truncate">{booking.account?.identifier || "-"}</Typography>,
        company: <Typography className="truncate">{booking.account?.company?.name || "-"}</Typography>,
        pickupLocationFull: renderPickupLocation(booking.stops, booking.metadata.isVerified, booking.flightNotFlying),
        // pickupLocationSuburb: renderPickupLocation(booking.stops, booking.metadata.isVerified),
        destinationFull: renderDestination(
          booking.stops[booking.stops.length - 1].address,
          booking.metadata.isVerified,
          departureFlightOf(booking.stops)
        ),
        // destinationSuburb: renderDestination(booking.stops[booking.stops.length - 1].address, booking.metadata.isVerified),
        driver: renderDriver(
          booking.metadata.vehicleCorrectErrors,
          booking.metadata.isVehicleCorrect,
          booking.driver,
          booking.assignedVehicle
        ),
        transferType: <div className="truncate">{booking.transferType.name}</div>,
        cost:
          (!booking.metadata.isOffloaded || providerKey === booking.operator?.providerKey.toLowerCase()) && booking.cost
            ? formatCurrency(booking.cost.total, 2, booking.cost.currency)
            : "-",
        status: renderBookingStatus(booking.status, booking.metadata.isAppBooking, booking.closeMeta),
        paymentStatus: booking.paymentStatus ? paymentStatuses[booking.paymentStatus] : "",
        paymentMethod: booking.cost?.paymentMethod ? paymentMethods[booking.cost.paymentMethod] || booking.cost.paymentMethod : "-",
        dashboardNotes: booking.notes.dashboard,
        operator: <Typography className="truncate">{booking.operator?.name}</Typography>,
        vehicleType: <Typography className="truncate">{booking.vehicleCategory.name}</Typography>,
        metadata: {
          isVerified: booking.metadata.isVerified,
          transferRate: booking.transferType.type,
        },
      } as BookingsTableData)
  );

  return data;
};

const renderOffloadStatus = (booking: Booking, providerKey: string) => {
  const isOwnJob = booking.operator?.providerKey.toLowerCase() === providerKey.toLowerCase();
  const { isInPrivateNetwork, isOffloadToPrivate, isOffloadToPublic, isOffloaded } = booking.metadata;

  const iconConfig = {
    ownJobPublic: {
      name: "booking-offloaded-network",
      content: "Offloaded to the network",
      show: isOwnJob && isOffloaded && !isInPrivateNetwork,
    },
    ownJobPrivate: {
      name: "booking-offloaded-private-network",
      content: "Offload through your Private Network",
      show: isOwnJob && isOffloaded && isInPrivateNetwork,
    },
    acceptedPublic: {
      name: "booking-accepted-network",
      content: "Accepted from the network",
      show: !isOwnJob && (isOffloadToPublic || (isOffloaded && !isInPrivateNetwork)),
    },
    acceptedPrivate: {
      name: "booking-accepted-private-network",
      content: "Accepted from your Private Network",
      show: !isOwnJob && (isOffloadToPrivate || (isOffloaded && isInPrivateNetwork)),
    },
  };

  const activeConfig = Object.values(iconConfig).find((config) => config.show);

  if (!activeConfig) return null;

  return (
    <Tooltip content={activeConfig.content}>
      <Icon name={activeConfig.name} isCustom size={18} className="-mb-3 text-neutral-dark-gray sm:mb-0" />
    </Tooltip>
  );
};

// Passenger names come back from the API with the honorific prefix folded in ("Mr. Jane Doe"),
// but bookedBy never carries one — strip it before comparing the two.
const passengerTitles = ["Mr.", "Mrs.", "Ms.", "Miss", "Dr.", "Prof.", "Sir."];

const stripPassengerTitle = (name: string) => {
  const title = passengerTitles.find((t) => name.startsWith(`${t} `));
  return title ? name.slice(title.length + 1) : name;
};

/**
 * The provenance caption under the pickup time — "incl. 20 min driver delay" (design node
 * `11784:129922`). Accent, 12/16 medium, no icon and no label, and always the LAST line in the cell:
 * time → "(Perth)" → caption.
 *
 * It INHERITS the operator panel's falsehood guard rather than rendering unconditionally as the design
 * draws it. `BasicDetailsSection` suppresses the same claim when it holds a derived arrival that does
 * NOT add up to the pickup, because something moved the pickup after it was derived and "incl. 20 min"
 * may itself be false. The dashboard row carries all three fields (`transformBookings`,
 * `redux/apis/booking/helpers.ts:191-193`), so the check is computable here too — and the design's own
 * mock is the failure case it catches (`At Gate 11:57 PM` + `20 mins` ≠ `8:45 AM`).
 *
 * The guard keys on the arrival TIME alone, where the panel keys on the arrival AND its stored kind.
 * Deliberate: the panel needs the kind because it NAMES the arrival in a visible row, while this cell
 * only needs to know whether the arithmetic disagrees. Requiring the kind here would show the caption
 * on a row whose sum is checkable and wrong.
 *
 * Gated to `sm:` and up, and hidden in compact view — exactly the `(Perth)` timezone qualifier beside
 * it (`hidden group-[.compact-view]:hidden sm:inline`). Both are secondary lines on a cell whose job at
 * small widths is the time itself, every neighbouring cell truncates to one line in compact view, and
 * the design node is the normal table. The mobile gate is also what keeps the caption LAST in the
 * stack: the cell ends with an `sm:hidden` field label, so an ungated caption would sit between
 * the value and its own label on mobile.
 *
 * Shared by BOTH pickup-time cells — `time` and the combined `dateAndTime` — because `dateAndTime` is
 * the replacement an operator picks INSTEAD of `date` + `time`, not an addition to them. Rendering it
 * in one cell and not the other silently drops the provenance for anyone who made that column choice.
 */
const renderPickupDelayCaption = (booking: Booking, profile: Profile) => {
  const flight = booking.stops.find((stop) => stop.flight)?.flight;
  const heldArrival = flight?.pickupDerivedArrival;

  const contradicted =
    !!heldArrival &&
    !pickupReconcilesAsSum({
      arrivalIso: heldArrival,
      delayMinutes: flight?.pickupDelayMinutes,
      pickupIso: booking.dates.pickup,
      timezone: booking.dates.timezone,
      timeFormat: profile.timeFormat,
    });

  const caption = contradicted ? null : driverDelayCaption(flight?.pickupDelayMinutes);
  if (!caption) return null;

  return (
    <Typography variant="caption" className="hidden whitespace-nowrap text-info group-[.compact-view]:hidden sm:block">
      {caption}
    </Typography>
  );
};

/**
 * `operatorId` turns the name into a link to the client's profile — the in-app Manage Client record,
 * pairing with the job number's link to the booking sheet: the number opens the trip, the name opens
 * the person. Optional for the same reason as on the job number, and the name renders as plain text
 * without it rather than as a route with `undefined` in it.
 */
const renderPassenger = (booking: Booking, operatorId?: string) => {
  const { passenger, bookedBy, metadata, account } = booking;
  const shouldNeverOffload = account?.flags.dispatchRule === "never";
  const isVip = account?.flags.isVip || false;
  const isSelfBooking = bookedBy === passenger.name || bookedBy === stripPassengerTitle(passenger.name);

  return (
    <>
      <div className="">
        <Typography variant="paragraph" className="flex min-w-0 items-center leading-tight [&_a]:truncate">
          <Tooltip
            content={
              <div className="[&>p]:leading-tight">
                <Typography className="empty:hidden">Passenger: {passenger.name}</Typography>
                <Typography className="empty:hidden">Contact Number: {passenger.phone}</Typography>
              </div>
            }
            placement="bottom"
          >
            {/* Opens in a new tab, like the job number: both are ways OUT of a row you are still
                working through, and losing the table's filters and scroll position to look up one
                client is the thing that makes an operator not click it.
                Still a react-router Link rather than an anchor — getManageClientPath is an in-app
                route, so Link is what applies the /dashboard basename to the href. */}
            {operatorId && account ? (
              <Link
                to={getManageClientPath(operatorId, account.id)}
                className="relative z-10 hover:text-info"
                onClick={(e) => e.stopPropagation()}
                target="_blank"
                rel="noopener noreferrer"
              >
                {passenger.name} {passenger.count > 1 && `(${passenger.count})`}
              </Link>
            ) : (
              <span>
                {passenger.name} {passenger.count > 1 && `(${passenger.count})`}
              </span>
            )}
          </Tooltip>

          {shouldNeverOffload && (
            <Tooltip content="Never Offload" placement="bottom">
              <Icon name="Flag" variant="Bold" size="sm" className="ml-1 inline text-info" />
            </Tooltip>
          )}

          {isVip && (
            <Tooltip content="VIP Client" placement="bottom">
              <Icon name="Crown" variant="Bold" size="sm" className="ml-1 inline text-warning" />
            </Tooltip>
          )}
        </Typography>

        {!isSelfBooking && !metadata.isAcceptedFromNetwork && (
          <Typography
            variant="small-regular"
            className="line-clamp-1 hidden leading-snug text-neutral-dark-gray group-[.compact-view]:!hidden sm:block"
          >
            Booked by {bookedBy}
          </Typography>
        )}
      </div>

      <Typography variant="small" className="block leading-normal text-neutral-dark-gray sm:hidden">
        Passenger
      </Typography>
    </>
  );
};

const renderBookingStatus = (status: BookingStatus, isAppBooking: boolean, closeMeta?: BookingCloseMeta) => {
  const statusObj = statusMapping[status];
  const closedAsNoShow = Boolean(closeMeta?.reason.includes("completed_no_show"));

  return (
    <div>
      <div className="flex items-center gap-1.5 2xl:gap-2 ">
        <Tooltip content={statusObj.name} placement="top">
          <i>
            <BookingStatusIcon
              status={{
                color: statusObj.color,
                icon: statusObj.icon,
              }}
            />
          </i>
        </Tooltip>
        <Typography variant="paragraph" className="leading-tight">
          {statusObj.legend}
        </Typography>
        {closedAsNoShow && (
          <Tooltip content="Closed as No Show" placement="top">
            <Icon size="sm" name="InfoCircle" variant="Bold" className="-translate-x-0.5 rotate-180 text-warning-dark" />
          </Tooltip>
        )}
        {isAppBooking && (
          <Tooltip content="Booked via Mobile" placement="bottom">
            <Icon name="app-booking" isCustom size={18} className="m-0" />
          </Tooltip>
        )}
      </div>
      <Typography variant="small" className="block leading-normal text-neutral-dark-gray sm:hidden">
        Status
      </Typography>
    </div>
  );
};

const renderFlightStatus = (flight?: BookingFlight | null) => {
  return flight ? <BookingFlightNumber number={flight.number} status={flight.status} delay={flight.delay} /> : null;
};

const renderPickupLocation = (stops: BookingStop[], isVerified: boolean, flightNotFlying: boolean) => {
  const pickup = stops[0];
  const midStops = stops.slice(1, stops.length - 1);
  // The ARRIVAL flight only. This used to search every stop, which was the same answer while the
  // pickup was the only stop that could carry one — and wrong once the drop-off could: a departure
  // booking hung its chip here, under a pickup the flight has nothing to do with.
  const flight = arrivalFlightOf(stops);

  return (
    <>
      <div className="flex flex-col">
        <Tooltip content={pickup.address} placement="bottom">
          <Typography variant="paragraph" className="line-clamp-1 leading-tight lg:line-clamp-2 lg:group-[.compact-view]:line-clamp-1">
            {pickup.address}
          </Typography>
        </Tooltip>
        <div className="mt-1 hidden items-center gap-2 empty:hidden lg:flex ">
          {!flight && flightNotFlying ? <BookingNotFlying /> : renderFlightStatus(flight)}
          {midStops.length > 0 && (
            <Typography variant="small" className="leading-snug text-neutral-dark-gray">
              {midStops.length > 0 && `+ ${midStops.length} Stop${midStops.length > 1 ? "s" : ""}`}
            </Typography>
          )}
        </div>
        {!isVerified && (
          <Typography variant="small" className="flex items-center text-danger [&+div]:hidden">
            Requires Validation
            <Icon name="Danger" size={18} variant="Bold" className="ml-1 inline text-warning" />
          </Typography>
        )}
      </div>
      <Typography variant="small" className="block leading-normal text-neutral-dark-gray sm:hidden">
        Pickup
      </Typography>
    </>
  );
};

const renderDestination = (address: string, isVerified: boolean, flight?: BookingFlight | null) => {
  return (
    <>
      <div className="flex flex-col ">
        <Tooltip content={address} placement="bottom">
          <Typography variant="paragraph" className="line-clamp-1 leading-tight lg:line-clamp-2 lg:group-[.compact-view]:line-clamp-1">
            {address}
          </Typography>
        </Tooltip>
        {/* The DEPARTURE flight, on the stop it actually belongs to. Same chip and the same breakpoint
            as the pickup's, so a job carrying both shows one at each end of the row. */}
        {flight && <div className="mt-1 hidden items-center gap-2 lg:flex">{renderFlightStatus(flight)}</div>}
        {!isVerified && (
          <Typography variant="small" className="flex items-center text-danger [&+div]:hidden">
            Requires Validation
            <Icon name="Danger" size={18} variant="Bold" className="ml-1 inline text-warning" />
          </Typography>
        )}
      </div>
      <Typography variant="small" className="block leading-normal text-neutral-dark-gray sm:hidden">
        Destination
      </Typography>
    </>
  );
};

const renderDriver = (
  vehicleCorrectErrors: string[],
  isVehicleCorrect: boolean,
  driver?: BookingDriver,
  assignedVehicle?: BookingVehicle
) => {
  const showWarning = assignedVehicle && driver?.vehicle ? assignedVehicle.uuid !== driver.vehicle.uuid : !isVehicleCorrect;
  return (
    <>
      {driver ? (
        <Tooltip
          content={
            <div className="[&>p]:leading-tight">
              <Typography>Driver: {driver.name}</Typography>
              <Typography>Number: {driver.phone}</Typography>
            </div>
          }
          placement="bottom"
        >
          <Typography className="line-clamp-1 inline-flex items-center leading-tight group-[.compact-view]:whitespace-nowrap max-sm:truncate lg:line-clamp-2">
            {driver.name}
            {showWarning && (
              <Tooltip content={getVehicleWarningTooltip(vehicleCorrectErrors)} placement="top">
                <Icon name="Danger" size={18} variant="Bold" className="ml-1 inline text-warning" />
              </Tooltip>
            )}
          </Typography>
        </Tooltip>
      ) : (
        <Typography className="truncate leading-tight">-</Typography>
      )}
      <Typography variant="small" className="block leading-normal text-neutral-dark-gray sm:hidden">
        Driver
      </Typography>
    </>
  );
};

export const renderVerificationBadge = (isVerified: boolean) => {
  if (isVerified) {
    return (
      <Typography variant="action" className="flex items-center">
        <Tooltip content="Vehicle is verified" placement="top">
          <Icon name="Verify" variant="Bold" size="sm" className="text-info" />
        </Tooltip>
      </Typography>
    );
  } else {
    return (
      <Typography variant="action" className="flex items-center">
        <Tooltip content="Vehicle is unverified" placement="top">
          <Icon name="Verify" variant="Bold" size="sm" className="text-neutral-mid-gray" />
        </Tooltip>
      </Typography>
    );
  }
};
